import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from 'src/common/axiosInstance';
import { FaClock, FaCheckCircle, FaTimesCircle, FaTasks, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const DetailView = () => {
  const { id } = useParams();
  const [details, setDetails] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const response = await axiosInstance.get(`/workflow-execution/${id}`);
        if (response.status === 200) {
          const data = response.data;
          setDetails(data);
        } else {
          console.error('Unexpected status code:', response.status);
        }
      } catch (error) {
        if (error.response) {
          console.error('Server error response:', error.response);
        } else if (error.request) {
          console.error('No response received:', error.request);
        } else {
          console.error('Error setting up request:', error.message);
        }
      }
    };

    fetchDetails();
  }, [id]);

  const handleExpandClick = (index) => {
    const newExpandedRows = new Set(expandedRows);
    if (expandedRows.has(index)) {
      newExpandedRows.delete(index);
    } else {
      newExpandedRows.add(index);
    }
    setExpandedRows(newExpandedRows);
  };

  const renderData = (data) => {
    // Handle different data types for Input and Output Data
    if (typeof data === 'string') {
      // Likely a primitive string value
      return data;
    } else if (Array.isArray(data)) {
      // Handle array data (assuming you want to display it as a list)
      return (
        <ul>
          {data.map((item, subIndex) => (
            <li key={subIndex}>{renderData(item)}</li>
          ))}
        </ul>
      );
    } else if (typeof data === 'object') {
      // Try to parse JSON and display a formatted string
      try {
        const parsedData = JSON.stringify(data, null, 2);
        return (
          <pre style={{ whiteSpace: 'pre-wrap' }}>{parsedData}</pre>
        );
      } catch {
        return <div>Invalid JSON data</div>;
      }
    } else {
      // Handle unexpected data types with a message
      return <div>Unknown data type</div>;
    }
  };

  return (
    <div className="overflow-x-auto w-full p-4">
      <div className="inline-block min-w-full py-2 align-middle">
        <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task Details</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Error</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Input Data</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Output Data</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {details.map((item, index) => (
                <React.Fragment key={index}>
                  <tr className="cursor-pointer hover:bg-gray-100">
                    <td className="px-6 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 text-gray-900">
                      {item.taskTemplateName}
                    </td>
                    <td className="px-6 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 text-gray-900">
                      {item.status}
                    </td>
                    <td className="px-6 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 text-gray-900">
                      {item.errorMessage || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 text-gray-900">
                      <button onClick={() => handleExpandClick(index)} className="text-blue-500 underline">
                        {expandedRows.has(index) ? 'Show Less' : 'Show More'}
                        {expandedRows.has(index) ? <FaChevronUp className="inline-block ml-1" /> : <FaChevronDown className="inline-block ml-1" />}
                      </button>
                      {expandedRows.has(index) && (
                        <div className="mt-2 p-2 border border-gray-300 rounded bg-gray-50">
                          <h4 className="text-sm font-medium">Input Data:</h4>
                          <pre>{renderData(item.inputData)}</pre>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 text-gray-900">
                      <button onClick={() => handleExpandClick(index)} className="text-blue-500 underline">
                        {expandedRows.has(index) ? 'Show Less' : 'Show More'}
                        {expandedRows.has(index) ? <FaChevronUp className="inline-block ml-1" /> : <FaChevronDown className="inline-block ml-1" />}
                      </button>
                      {expandedRows.has(index) && (
                        <div className="mt-2 p-2 border border-gray-300 rounded bg-gray-50">
                          <h4 className="text-sm font-medium">Output Data:</h4>
                          <pre style={{ whiteSpace: 'pre-wrap' }}>{renderData(item.outputData)}</pre>
                        </div>
                      )}
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DetailView;